import React, { useState, useEffect, useContext, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {ViewModelForm, ViewModelServiceContext} from '@xengage/gw-portals-viewmodel-react';
import { MLRiskAnalysisService } from 'ml-services-common-react';
import {DateValue, useModal} from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { Link } from '@jutro/router';
import {FormModalPopover} from "ml-components-platform-react";
import metadata from './MLRatingFactorContingenciesComponent.metadata.json5';
import messages from './MLRatingFactorContingenciesComponent.messages.js';
import mlDocumentsUploadPopupMetadata
    from "gw-capability-gateway-react/Policies/MLDocumentsUploadPopup/MLDocumentsUploadPopup.metadata.json5";
import MLDocumentsUploadPopupMsg
    from "gw-capability-gateway-react/Policies/MLDocumentsUploadPopup/MLDocumentsUploadPopup.messages";
import MLDocumentsUpload
    from "gw-capability-gateway-react/Policies/MLDocumentsUploadPopup/GatewayMLDocumentsUploadPopup";

const MLRatingFactorContingenciesComponent = (props) => {
    const modalApi = useModal();
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { jobOrPolicyNumber, isQuote, isPolicy, isPersonalAuto, isPersonalUmbrella, isHomeOwners, styles, authHeader, policy } = props;
    const [contingencies, setContingencies] = useState([]);

    const openUploadDocumentModal = useCallback((contingencyId, contingencyName, docSubtype) => {
        const componentProps = {
            showHeader: false,
            uiProps: mlDocumentsUploadPopupMetadata.componentContent,
            actionBtnLabel: _.upperCase(translator(MLDocumentsUploadPopupMsg.continue)),
            cancelBtnLabel: MLDocumentsUploadPopupMsg.cancelModal,
            disableButton: (vm) => !(_.get(vm, 'aspects.valid') && vm, _.get(vm, 'aspects.subtreeValid')),
            viewModelService,
            authHeader,
            CustomComponent: MLDocumentsUpload,
            overrideProps: {
                policyNumber: jobOrPolicyNumber,
                isAgent: true,
                policy,
                contingencyId,
                contingencyName,
                docSubtype
            },
            onSaveAction: () => {
                return false;
            },
            buttonType: 'secondary'
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    }, [authHeader, jobOrPolicyNumber, modalApi, policy, translator, viewModelService]);

    const updateContingencies = (data) => {
        if (isPersonalAuto) {
            setContingencies(data.lobs.personalAuto.contingencies);
        } else if (isHomeOwners) {
            setContingencies(data.lobs.homeowners.contingencies);
        } else if (isPersonalUmbrella) {
            setContingencies(data.lobs.personalUmbrella.contingencies);
        }
    };
    
    const getAndSetContingenciesBySubmissionNumber = (submissionNumber) => {
        if (_.isEmpty(contingencies)) {
            const promise = MLRiskAnalysisService.getRAContingencyDetailsMLByJobNumber(submissionNumber, authHeader);
            promise.then((data) => {
                updateContingencies(data);
            });
        }
    };

    const getAndSetContingenciesByPolicyNumber = (policyNumber) => {
        const promise = MLRiskAnalysisService.getRAContingencyDetailsMLByPolicyNumber(policyNumber, authHeader);
        promise.then((data) => {
            updateContingencies(data);
        });
    };

    useEffect(() => {
        if (isQuote) {
            getAndSetContingenciesBySubmissionNumber(jobOrPolicyNumber);
        } else if (isPolicy) {
            getAndSetContingenciesByPolicyNumber(jobOrPolicyNumber);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getContingenciesTableOverrides = () => {
        const getCellDateAdded = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.dateAdded} />;
        };
    
        const getCellDueDate = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.dueDate} />;
        };
    
        const getCellContingencyTitle = (rowData) => {
            if (rowData.isUploadDocumentType === true) {
                return <Link onClick={() => openUploadDocumentModal(rowData.publicId, rowData.contingencyName, rowData.documentSubtype)}>{rowData.title}</Link>;
            }
            return <span>{rowData.title}</span>;
        };
    
        const getCellDescription  = (rowData) => {
            if (rowData.isUploadDocumentType === true) {
                return <Link
                    onClick={() => openUploadDocumentModal(rowData.publicId, rowData.contingencyName, rowData.documentSubtype)}>{rowData.description ? rowData.description : '-'}</Link>;
            }
            return <span>{rowData.description ? rowData.description : '-'}</span>;
        };

        return {
            contingenciesTable: {
                data: contingencies,
                title: messages.actionRequired
            },
            dateAddedColumn: {
                renderCell: getCellDateAdded
            },
            dueDateColumn: {
                renderCell: getCellDueDate
            },
            contingencyTitleColumn: {
                renderCell: getCellContingencyTitle
            },
            descriptionColumn: {
                renderCell: getCellDescription
            }
        }
    }

    const overrideProps = {
        ...getContingenciesTableOverrides()
    }

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
        />    
    )
};

MLRatingFactorContingenciesComponent.propTypes = {
    jobOrPolicyNumber: PropTypes.string.isRequired,
    isQuote: PropTypes.bool.isRequired,
    isPolicy: PropTypes.bool.isRequired,
    isPersonalAuto: PropTypes.bool,
    isPersonalUmbrella: PropTypes.bool,
    isHomeOwners: PropTypes.bool,
    styles: PropTypes.arrayOf(PropTypes.shape({})),
    policy: PropTypes.shape({})
};

MLRatingFactorContingenciesComponent.defaultProps = {
    isPersonalAuto: false,
    isPersonalUmbrella: false,
    isHomeOwners: false,
    styles: undefined,
    policy: undefined
}

export default MLRatingFactorContingenciesComponent;